import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { WorkspaceAssignment } from "@common/types/workspace";
import { pluralize } from "@common/helpers/string";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import type {
    OccurrenceBulkActionType,
    OccurrenceBulkRoleAssignmentField,
} from "@common/schemas/serviceTemplateRecurrenceOccurrence.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    CommandsDirectory,
    InjectedCommandOption,
} from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import {
    useBulkOccurrenceActionMutation,
    useBulkUpdateOccurrenceRoleAssignmentMutation,
} from "@api/serviceTemplateScheduleOccurrence.api";
import type { ServiceTemplateScheduleOccurrenceRow } from "./serviceTemplateScheduleOccurrencesViewConfig";
import { getMemberLabel } from "./serviceTemplateScheduleMemberLabel";

interface UseServiceTemplateScheduleOccurrenceBulkActionsParams {
    organizationId: string | null | undefined;
    selectedRows: ServiceTemplateScheduleOccurrenceRow[];
    clearSelection: () => void;
    /** Any organization member — assignable as preparer/reviewer/manager (matches backend role validation). */
    assignableMembers: OrganizationMember[];
    /** Workspace-level assignments — assignable as client (matches backend client validation). */
    workspaceMembers: WorkspaceAssignment[];
}

/** Only SCHEDULED occurrences can be applied or skipped. */
const isEligibleForApplyOrSkip = (row: ServiceTemplateScheduleOccurrenceRow) =>
    row.status === ServiceTemplateRecurrenceOccurrenceStatus.SCHEDULED;

/** Only SKIPPED occurrences can be unskipped. */
const isEligibleForUnskip = (row: ServiceTemplateScheduleOccurrenceRow) =>
    row.status === ServiceTemplateRecurrenceOccurrenceStatus.SKIPPED;

/** APPLIED occurrences already produced a close — deletion happens from the work tab, not here. */
const isEligibleForDelete = (row: ServiceTemplateScheduleOccurrenceRow) =>
    row.status !== ServiceTemplateRecurrenceOccurrenceStatus.APPLIED;

export function useServiceTemplateScheduleOccurrenceBulkActions({
    organizationId,
    selectedRows,
    clearSelection,
    assignableMembers,
    workspaceMembers,
}: UseServiceTemplateScheduleOccurrenceBulkActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const { toast: successToast, error: errorToast } = useToaster();
    const bulkAction = useBulkOccurrenceActionMutation();
    const bulkRoleAssignment = useBulkUpdateOccurrenceRoleAssignmentMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const applySkipEligibleIds = useMemo(
        () =>
            selectedRows.filter(isEligibleForApplyOrSkip).map((row) => row.id),
        [selectedRows],
    );
    // Role overrides can only be set on SCHEDULED occurrences (matches
    // updateOccurrenceRoleOverride's invariant), so bulk role assignment
    // shares the same eligible-id set as apply/skip.
    const roleAssignmentEligibleIds = applySkipEligibleIds;
    const unskipEligibleIds = useMemo(
        () => selectedRows.filter(isEligibleForUnskip).map((row) => row.id),
        [selectedRows],
    );
    const deleteEligibleIds = useMemo(
        () => selectedRows.filter(isEligibleForDelete).map((row) => row.id),
        [selectedRows],
    );

    const runBulkAction = useCallback(
        async (
            occurrenceIds: string[],
            action: OccurrenceBulkActionType,
            verbPastTense: string,
        ) => {
            if (!organizationId || occurrenceIds.length === 0) {
                return;
            }
            closeCommands();
            const runAction = async () => {
                try {
                    const result = await bulkAction.mutateAsync({
                        organizationId,
                        occurrenceIds,
                        action,
                    });
                    if (result.succeededIds.length > 0) {
                        successToast(
                            `${verbPastTense} ${result.succeededIds.length} ${pluralize(
                                "occurrence",
                                result.succeededIds.length,
                            )}`,
                        );
                    }
                    if (result.failed.length > 0) {
                        errorToast(
                            `Failed to ${action} ${result.failed.length} ${pluralize(
                                "occurrence",
                                result.failed.length,
                            )}`,
                        );
                    }
                } catch (e) {
                    errorToast(
                        e instanceof Error
                            ? e.message
                            : "Failed to perform bulk action on occurrences",
                    );
                }
            };
            await runAction().finally(() => {
                clearSelection();
            });
        },
        [
            organizationId,
            bulkAction,
            closeCommands,
            successToast,
            errorToast,
            clearSelection,
        ],
    );

    const handleBulkApply = useCallback(
        () => void runBulkAction(applySkipEligibleIds, "apply", "Applied"),
        [runBulkAction, applySkipEligibleIds],
    );

    const handleBulkSkip = useCallback(
        () => void runBulkAction(applySkipEligibleIds, "skip", "Skipped"),
        [runBulkAction, applySkipEligibleIds],
    );

    const handleBulkUnskip = useCallback(
        () => void runBulkAction(unskipEligibleIds, "unskip", "Undid skip on"),
        [runBulkAction, unskipEligibleIds],
    );

    const handleBulkDeleteRequest = useCallback(() => {
        closeCommands();
        setShowDeleteConfirm(true);
    }, [closeCommands]);

    const handleBulkDeleteConfirm = useCallback(async () => {
        setShowDeleteConfirm(false);
        await runBulkAction(deleteEligibleIds, "delete", "Deleted");
    }, [runBulkAction, deleteEligibleIds]);

    const runBulkRoleAssignment = useCallback(
        async (
            field: OccurrenceBulkRoleAssignmentField,
            value: number | null,
        ) => {
            if (!organizationId || roleAssignmentEligibleIds.length === 0) {
                return;
            }
            closeCommands();
            const assignRole = async () => {
                try {
                    const result = await bulkRoleAssignment.mutateAsync({
                        organizationId,
                        occurrenceIds: roleAssignmentEligibleIds,
                        field,
                        value,
                    });
                    if (result.succeededIds.length > 0) {
                        successToast(
                            `Updated role assignment on ${result.succeededIds.length} ${pluralize(
                                "occurrence",
                                result.succeededIds.length,
                            )}`,
                        );
                    }
                    if (result.failed.length > 0) {
                        errorToast(
                            `Failed to update role assignment on ${result.failed.length} ${pluralize(
                                "occurrence",
                                result.failed.length,
                            )}`,
                        );
                    }
                } catch (e) {
                    errorToast(
                        e instanceof Error
                            ? e.message
                            : "Failed to update role assignment on occurrences",
                    );
                }
            };
            await assignRole().finally(() => {
                clearSelection();
            });
        },
        [
            organizationId,
            roleAssignmentEligibleIds,
            bulkRoleAssignment,
            closeCommands,
            successToast,
            errorToast,
            clearSelection,
        ],
    );

    const buildMemberOptions = useCallback(
        (
            field: OccurrenceBulkRoleAssignmentField,
            members: OrganizationMember[] | WorkspaceAssignment[],
        ): InjectedCommandOption[] => {
            const noneOption: InjectedCommandOption = {
                id: `occurrence-bulk-${field}-none`,
                label: "None",
                keywords: ["none", "clear", "remove", "unassign"],
                onSelect: () => void runBulkRoleAssignment(field, null),
            };
            const memberOptions: InjectedCommandOption[] = members.map((m) => {
                const label = getMemberLabel(m);
                return {
                    id: `occurrence-bulk-${field}-${m.user.id}`,
                    label,
                    keywords: [label.toLowerCase()],
                    onSelect: () =>
                        void runBulkRoleAssignment(field, m.user.id),
                };
            });
            return [noneOption, ...memberOptions];
        },
        [runBulkRoleAssignment],
    );

    const preparerOptions = useMemo(
        () =>
            roleAssignmentEligibleIds.length > 0
                ? buildMemberOptions("preparerId", assignableMembers)
                : undefined,
        [
            roleAssignmentEligibleIds.length,
            buildMemberOptions,
            assignableMembers,
        ],
    );
    const reviewerOptions = useMemo(
        () =>
            roleAssignmentEligibleIds.length > 0
                ? buildMemberOptions("reviewerId", assignableMembers)
                : undefined,
        [
            roleAssignmentEligibleIds.length,
            buildMemberOptions,
            assignableMembers,
        ],
    );
    const managerOptions = useMemo(
        () =>
            roleAssignmentEligibleIds.length > 0
                ? buildMemberOptions("managerId", assignableMembers)
                : undefined,
        [
            roleAssignmentEligibleIds.length,
            buildMemberOptions,
            assignableMembers,
        ],
    );
    const clientOptions = useMemo(
        () =>
            roleAssignmentEligibleIds.length > 0
                ? buildMemberOptions("clientId", workspaceMembers)
                : undefined,
        [
            roleAssignmentEligibleIds.length,
            buildMemberOptions,
            workspaceMembers,
        ],
    );

    const handleOpenBulkActions = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        openCommands(CommandsDirectory.OCCURRENCE_BULK, {
            selectedOccurrenceCount: selectedRows.length,
            onOccurrenceBulkApply:
                applySkipEligibleIds.length > 0 ? handleBulkApply : undefined,
            onOccurrenceBulkSkip:
                applySkipEligibleIds.length > 0 ? handleBulkSkip : undefined,
            onOccurrenceBulkUnskip:
                unskipEligibleIds.length > 0 ? handleBulkUnskip : undefined,
            onOccurrenceBulkDelete:
                deleteEligibleIds.length > 0
                    ? handleBulkDeleteRequest
                    : undefined,
            occurrenceBulkPreparerOptions: preparerOptions,
            occurrenceBulkReviewerOptions: reviewerOptions,
            occurrenceBulkManagerOptions: managerOptions,
            occurrenceBulkClientOptions: clientOptions,
        });
    }, [
        openCommands,
        selectedRows.length,
        applySkipEligibleIds.length,
        unskipEligibleIds.length,
        deleteEligibleIds.length,
        handleBulkApply,
        handleBulkSkip,
        handleBulkUnskip,
        handleBulkDeleteRequest,
        preparerOptions,
        reviewerOptions,
        managerOptions,
        clientOptions,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedRows.length > 0) {
            registerOpenOverride(() => handleOpenBulkActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [selectedRows.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount: deleteEligibleIds.length,
        isBulkActionPending:
            bulkAction.isPending || bulkRoleAssignment.isPending,
    };
}
